import { Controller, useForm, UseFormSetValue } from 'react-hook-form'
import {
  ProductTicketLotCriterion,
  ProductTicketLotVM,
} from '../../../../../core/types/Tickets'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SwipeableDrawerRightDialog from '../../../../../components/Dialogs/SwipeableDrawerRightDialog'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { lotCriterionConfig, lotCriterionValues } from './ProductTicketConsts'
import { SelectWithCreate } from '../../../../../components/SelectInput/SelectWithCreate'
import {
  TPaymentMethodEnum,
  TProduct,
  TProductDelivery,
  TProductFrequency,
  TProductPrice,
  TProductPricePayment,
  TProductTypePayment,
} from '../../../../../core/types/Product'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import ProductPriceDetail from '../ProductPrice/ProductPriceDetail'
import { productPriceDefault } from '../ProductPrice/ProductPriceConsts'
import { processPayment } from '../../../utils/paymentProcessor'
import ProductFunctions from '../../../../../core/functions/ProductFunctions'
import { IMultiSelectedValueItem } from '../../../../../components/Dialogs/MultiSelectedValueDialog'
import { IPayment } from '../../../../../core/types/Payment'
import PaymentController from '../../../../../core/controllers/PaymentController'
import { IErrorResponse } from '../../../../../core/types/ErrorResponse'
import ProductController from '../../../../../core/controllers/ProductController'
import Hosts, { payUrl } from '../../../../../core/functions/hosts'
import MultiSelectedDialog, {
  MultiSelectedItem,
} from '../../../../../components/Dialogs/MultiSelectedDialog'
import OrderBumpIcon from '@mui/icons-material/Discount'
import { IOrderBump } from '../../../../../core/types/OrderBump'
import LoadingBackdrop from '../../../../../components/Loading/LoadingBackdrop'
import CopiedSnackbar from '../../../../../components/Snackbar/CopiedSnackbar'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'

export default function ProductTicketLotDialog({
  open,
  initial,
  productId,
  offers,
  typePaymentValue,
  delivery,
  productMaximumQuantity,
  productOrderBumps,
  onClose,
  onSubmit,
  setProductOrderBumps,
  setProdValue,
}: {
  open: boolean
  initial?: ProductTicketLotVM
  productId: string
  offers: TProductPrice[] | undefined
  typePaymentValue: TProductTypePayment | undefined
  delivery: TProductDelivery
  productMaximumQuantity?: number | null | undefined
  productOrderBumps: MultiSelectedItem[]
  onClose: () => void
  onSubmit: (l: ProductTicketLotVM) => void
  setProductOrderBumps: (items: MultiSelectedItem[]) => void
  setProdValue: UseFormSetValue<TProduct>
}) {
  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ProductTicketLotVM>({
      defaultValues: initial,
      mode: 'onBlur',
    })

  const criterionValue = watch('criterion')
  const hasInterval = criterionValue !== ProductTicketLotCriterion.quantity
  const hasQuantity = criterionValue !== ProductTicketLotCriterion.date

  const [openNewOffer, setOpenNewOffer] = useState(false)
  const [current, setCurrent] = React.useState<TProductPrice>({
    ...productPriceDefault,
  })
  const [payments, setPayments] = React.useState<IPayment[]>([])
  const [methodTypes, setMethodTypes] = React.useState<
    IMultiSelectedValueItem[]
  >([])
  const [options, setOptions] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [linkCopied, setLinkCopied] = useState('')
  const [openOrderBumps, setOpenOrderBumps] = React.useState(false)

  const memo = useMemo(
    () => ({
      offers:
        offers
          ?.filter((o) => !o.disabled)
          .map((o) => ({
            id: o.id,
            name: `${o.offerName} - ${NumberFunctions.formatMoney(o.value)}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)) || [],
    }),
    [offers]
  )

  const actionLinkCopied = (
    <React.Fragment>
      <Button
        color="secondary"
        size="small"
        onClick={() => Hosts.openNewBlank(linkCopied)}
        sx={{ color: 'white' }}
      >
        ABRIR LINK
      </Button>
    </React.Fragment>
  )

  const openOffer = () => {
    setCurrent({
      ...productPriceDefault,
    })
    setOpenNewOffer(true)
  }

  const handlePayments = async () => {
    const value = current.value

    if (!value || value <= 0 || value < 5) {
      setMethodTypes([])
      return
    }

    if (
      typePaymentValue === TProductTypePayment.RecurringSubscription &&
      !current.frequency
    ) {
      setMethodTypes([])
      return
    }

    let pays = payments

    if (pays.length === 0) {
      const data = await PaymentController.getAll()
      if ((data as IErrorResponse).code) return
      pays = data as IPayment[]
      setPayments(pays)
    }

    const total = ProductFunctions.maxPaymentInstallment(
      typePaymentValue,
      current.frequency,
      value
    )

    const options = Array.from({ length: total }, (_, i) => i + 1)

    const newList = pays.map((p) =>
      processPayment(
        p,
        current,
        options,
        typePaymentValue || TProductTypePayment.OneTimePayment
      )
    )

    setOptions(options)
    setMethodTypes(newList)
  }

  const handleChangeOffer = useCallback(async () => {
    if (!productId) {
      return
    }
    setLoading(true)
    try {
      if (current) {
        current.productId = productId
        const response = await ProductController.insertPrice({ data: current })
        if (!response.success) {
          setError(response.error)
          return
        }
        const dataPrices = await ProductController.getPricesByProduct({
          productId,
        })
        if (dataPrices) {
          setProdValue('prices', dataPrices as TProductPrice[])
        }
        setOpenNewOffer(false)
      }
    } finally {
      setLoading(false)
    }
  }, [current, watch, setLoading, setValue])

  const handleSavePrice = useCallback(() => {
    if (!current.value || current.value < 5) {
      setError(
        `O preço do produto precisa ser no mínimo de ${NumberFunctions.formatMoneyDefault(5)}`
      )
      return
    }
    if (
      current.paymentMethods === undefined ||
      current.paymentMethods.length <= 0
    ) {
      setError(
        `O preço ${NumberFunctions.formatMoneyDefault(
          current.value
        )} do produto precisa ter pelo menos uma forma de pagamento`
      )
      return
    }
    if (typePaymentValue === TProductTypePayment.RecurringSubscription) {
      if (!current.frequency || current.frequency === null) {
        setError(
          `O preço ${NumberFunctions.formatMoneyDefault(
            current.value
          )} do produto precisa selecionar uma frequência de cobrança`
        )
        return
      }

      if (current.frequency !== TProductFrequency.Yearly) {
        current.paymentMethods = current.paymentMethods.filter((pm) =>
          [TPaymentMethodEnum.CreditCard, TPaymentMethodEnum.Pix].includes(
            pm.methodType
          )
        )
      }
    }

    const paymentCreditCard = current.paymentMethods.find(
      (p) => p.methodType === TPaymentMethodEnum.CreditCard
    )
    if (paymentCreditCard) {
      const total = ProductFunctions.maxPaymentInstallment(
        typePaymentValue,
        current.frequency,
        current.value
      )

      if (paymentCreditCard.installmentsNumber > total) {
        paymentCreditCard.installmentsNumber = total
      }

      if (current.value < 10) {
        current.paymentMethods = current.paymentMethods.filter(
          (pm) =>
            ![
              TPaymentMethodEnum.TwoCreditCard,
              TPaymentMethodEnum.CreditCardAndPix,
            ].includes(pm.methodType)
        )
      }
    }

    setCurrent({ ...productPriceDefault })
    handleChangeOffer()
  }, [current, setCurrent, watch])

  const handleSavePayments = (
    data: IMultiSelectedValueItem[],
    productPrice: TProductPrice
  ) => {
    const paymentMethods = current.paymentMethods || []
    const newPayments: TProductPricePayment[] = []

    const notSelecteds = data.filter((d) => d.selected === false)
    paymentMethods.map((payment) => {
      const exists = notSelecteds.find(
        (ns) => Number(ns.id) === payment.methodType
      )
      if (!exists) {
        newPayments.push({ ...payment })
      }
      return true
    })

    data.map((d) => {
      if (d.selected) {
        const filtered = newPayments.find(
          (payment) => payment.methodType === Number(d.id)
        )
        if (!filtered) {
          newPayments.push({
            methodType: Number(d.id),
            description: d.name,
            installmentsNumber: d.value,
            installments: [],
          })
        } else {
          filtered!.installmentsNumber = d.value
        }
      }
      return true
    })

    setCurrent({ ...productPrice, paymentMethods: newPayments })
  }

  const handleChangeCopyURL = async (producerLink: boolean, index?: number) => {
    const url = `${payUrl}${producerLink ? '/pro' : ''}/${current.codeId}`
    await Hosts.copyTextToClipboard(url)
    setLinkCopied(url)
  }

  const handleOrderBumps = () => {
    var newList: MultiSelectedItem[] = []
    productOrderBumps.map((productOrderBump) => {
      const exists = current.orderBumps?.filter(
        (ob) => ob.orderBumpId === productOrderBump.id
      )
      newList.push({
        id: productOrderBump.id,
        name: productOrderBump.name,
        selected: exists !== undefined && exists.length > 0,
        object: productOrderBump.object,
        order:
          exists !== undefined && exists.length > 0
            ? exists[0].order
            : undefined,
      })
      return true
    })

    newList = newList.sort((a, b) => (a.order || 0) - (b.order || 0))
    newList = newList.sort((a, b) =>
      (a.order || 0) === 0 || (b.order || 0) === 0 ? -1 : 0
    )

    setProductOrderBumps(newList)
    setOpenOrderBumps(true)
  }

  const closeOrderBumps = () => {
    setOpenOrderBumps(false)
  }

  const handleSaveOrderBumps = (data: MultiSelectedItem[]) => {
    if (!current.orderBumps || current.orderBumps === null) {
      current.orderBumps = []
    }

    const auxOrderBumps = [...current.orderBumps]
    auxOrderBumps.map((aux) => {
      const datas = data.filter((d) => d.id === aux.orderBumpId && !d.selected)
      if (datas.length > 0) {
        current.orderBumps = current.orderBumps!.filter(
          (ob) => ob.orderBumpId !== aux.orderBumpId
        )
      }
      return true
    })

    data.map((d) => {
      if (d.selected) {
        const filtered = current.orderBumps!.filter(
          (orderBump) => orderBump.orderBumpId === d.id
        )
        const list = productOrderBumps.filter(
          (orderBump) => orderBump.object.id === d.id
        )
        const positioned = list[0].object as IOrderBump
        if (filtered.length <= 0) {
          current.orderBumps!.push({
            id: d.id,
            orderBumpId: d.id,
            order: d.order,
            productPrice: {
              id: positioned.productPrice?.id || '',
              value: positioned.productPrice?.value,
              product: {
                id: positioned.productPrice?.product?.id || '',
                name: positioned.productPrice?.product?.name || '',
              },
            },
          })
        } else {
          filtered[0].order = d.order
        }
      }
      return true
    })

    setCurrent({ ...current })
  }

  useEffect(() => {
    // Atualiza valores quando o initial mudar
    reset(initial)
  }, [initial])

  useEffect(() => {
    handlePayments()
  }, [current])

  return (
    <>
      <SwipeableDrawerRightDialog
        open={open}
        setOpen={(value) => onClose()}
        onClose={onClose}
        title="Lote"
        subtitle="Defina as configurações do lote"
        buttons={[
          { title: 'Cancelar', type: 'negative', onClick: onClose },
          {
            title: 'Continuar',
            type: 'positive',
            onClick: handleSubmit(onSubmit),
          },
        ]}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'O nome do lote é obrigatório' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    label="Nome do Lote"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="offerId"
                control={control}
                rules={{ required: 'A oferta é obrigatória' }}
                render={({ field, fieldState }) => (
                  <SelectWithCreate
                    label="Oferta"
                    labelCreate="Nova oferta"
                    options={memo.offers}
                    value={field.value}
                    onChange={(value) => {
                      const offer = offers?.find((o) => o.id === value)
                      if (offer) {
                        setValue('offerId', value)
                        setValue('offerCodeId', offer.codeId || '')
                        setValue('offerName', offer.offerName || '')
                        setValue('offerValue', offer.value || 0)
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onCreate={openOffer}
                  />
                )}
              />
            </Grid>
            {/*<Grid item xs={6} md={4}>
            <Controller
              name="price"
              control={control}
              rules={{
                required: 'O preço é obrigatório',
                min: {
                  value: 5,
                  message: 'O preço deve ser no mínimo R$ 5,00.',
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  type="number"
                  label="Preço (R$)"
                  fullWidth
                  inputProps={{ step: '0.01' }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>*/}
            <Grid item xs={6} md={6}>
              <Controller
                name="order"
                control={control}
                rules={{
                  required: 'O campo ordem é obrigatório',
                  min: {
                    value: 1,
                    message: 'É necessário ser igual ou maior que 1',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="number"
                    label="Ordem"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="criterion"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Critério</InputLabel>
                    <Select {...field} label="Critério">
                      {lotCriterionValues.map((value) => (
                        <MenuItem value={value}>
                          {
                            lotCriterionConfig[
                              value as ProductTicketLotCriterion
                            ].description
                          }
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {hasInterval && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      required: hasInterval && 'O campo início é obrigatório',
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        type="datetime-local"
                        label="Início"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      required: hasInterval && 'O campo fim é obrigatório',
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        size="small"
                        type="datetime-local"
                        label="Fim"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {hasQuantity && (
              <Grid item xs={12}>
                <Controller
                  name="unitLimit"
                  control={control}
                  rules={{
                    required: hasQuantity && 'O campo limite é obrigatório',
                    min: { value: 1, message: 'É necessário ser no mínimo 1' },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      size="small"
                      type="number"
                      label="Limite de unidades"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </SwipeableDrawerRightDialog>

      <ProductPriceDetail
        open={openNewOffer}
        sending={false}
        prices={offers || []}
        price={current}
        buttonPositiveTitle="Salvar"
        typePaymentValue={
          typePaymentValue ?? TProductTypePayment.OneTimePayment
        }
        isTenantBank={true}
        setOpen={setOpenNewOffer}
        handleClose={() => setOpenNewOffer(false)}
        setPrice={setCurrent}
        onSave={handleSavePrice}
        onCopyURL={handleChangeCopyURL}
        onOrderBump={handleOrderBumps}
        methodTypes={methodTypes}
        setMethodTypes={setMethodTypes}
        onSavePayments={handleSavePayments}
        options={options}
        delivery={delivery}
        productMaximumQuantity={productMaximumQuantity}
        showCurrencyField={false}
      />

      <MultiSelectedDialog
        open={openOrderBumps}
        title="Selecione os Order Bumps"
        description="Pressione e arraste um item selecionado para alterar a order"
        icon={<OrderBumpIcon />}
        list={productOrderBumps}
        setList={setProductOrderBumps}
        onClose={closeOrderBumps}
        onSave={handleSaveOrderBumps}
      />

      <LoadingBackdrop open={loading} />

      <CopiedSnackbar
        open={!!linkCopied}
        onClose={() => setLinkCopied('')}
        action={actionLinkCopied}
      />

      <ErrorSnackbar open={!!error} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>
    </>
  )
}
